<template>
  <div
    v-if="open"
    class="tc-sources-backdrop"
    role="dialog"
    aria-modal="true"
    aria-label="Sources"
    data-testid="sources-modal"
  >
    <div class="tc-sources-modal">
      <header class="tc-sources-modal-header">
        <h2 class="tc-sources-modal-title">Sources</h2>
        <button
          type="button"
          class="tc-admin-modal-close"
          aria-label="Close"
          @click="emit('close')"
        >&times;</button>
      </header>

      <div v-if="isLoading" class="tc-sources-loading">
        <span class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></span>
        Loading sources…
      </div>

      <div v-else-if="isError" class="tc-sources-error" role="alert" data-testid="sources-error">
        {{ errorMessage }}
      </div>

      <ul v-else class="tc-sources-list" data-testid="sources-list">
        <li
          v-for="source in enrichedSources"
          :key="source.id"
          class="tc-sources-row"
          :data-testid="`sources-row-${source.id}`"
        >
          <!-- Card body: tile + info + dot + actions -->
          <div class="tc-sources-card">
            <!-- 36px icon tile -->
            <SourceBadge :source="(source.id as SourceId)" class="tc-sources-tile" />

            <!-- Info column -->
            <div class="tc-sources-info">
              <span class="tc-sources-name">{{ source.label }}</span>
              <span class="tc-sources-detail" :data-testid="`sources-detail-${source.id}`">{{ source.detailLine }}</span>
            </div>

            <!-- Status dot -->
            <span
              class="tc-sources-dot"
              :class="`tc-sources-dot--${dotClass(source.status)}`"
              :data-testid="`sources-pill-${source.id}`"
              :title="pillLabel(source.status)"
              :aria-label="pillLabel(source.status)"
            ></span>

            <!-- Actions -->
            <div class="tc-sources-actions">
              <template v-if="source.connectKind === 'unavailable'">
                <span class="tc-sources-soon" :data-testid="`sources-soon-${source.id}`">Coming soon</span>
              </template>
              <template v-else-if="source.status === 'connected'">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="disconnectMutation.isPending.value && disconnectingId === source.id"
                  :data-testid="`sources-disconnect-${source.id}`"
                  @click="handleDisconnect(source.id, source.label)"
                >
                  <span
                    v-if="disconnectMutation.isPending.value && disconnectingId === source.id"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Disconnect
                </button>
              </template>
              <template v-else-if="source.status === 'upstream_error'">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="disconnectMutation.isPending.value && disconnectingId === source.id"
                  :data-testid="`sources-disconnect-${source.id}`"
                  @click="handleDisconnect(source.id, source.label)"
                >
                  <span
                    v-if="disconnectMutation.isPending.value && disconnectingId === source.id"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Disconnect
                </button>
              </template>
              <template v-else-if="source.status === 'not_connected'">
                <button
                  v-if="source.connectKind === 'oauth_popup'"
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :data-testid="`sources-connect-${source.id}`"
                  @click="connectOAuthPopup(source.id)"
                >Connect</button>
                <button
                  v-else-if="source.connectKind === 'api_key_form'"
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :data-testid="`sources-connect-${source.id}`"
                  @click="openForm(source.id)"
                >Connect</button>
              </template>
              <template v-else-if="source.status === 'reauth_required'">
                <button
                  v-if="source.connectKind === 'oauth_popup'"
                  type="button"
                  class="btn btn-primary btn-sm"
                  :data-testid="`sources-reconnect-${source.id}`"
                  @click="connectOAuthPopup(source.id)"
                >Reconnect</button>
                <button
                  v-else-if="source.connectKind === 'api_key_form'"
                  type="button"
                  class="btn btn-primary btn-sm"
                  :data-testid="`sources-reconnect-${source.id}`"
                  @click="openForm(source.id)"
                >Reconnect</button>
              </template>
            </div>
          </div>

          <!-- Inline Freshdesk form — expands below the card -->
          <FreshdeskConnectForm
            v-if="expandedFormId === source.id"
            @cancel="expandedFormId = null"
            @connected="expandedFormId = null"
          />
        </li>
      </ul>

      <div class="tc-sources-footer">
        <button type="button" class="btn btn-outline-secondary" @click="emit('close')">Close</button>
      </div>
    </div>

    <!-- Confirm dialog -->
    <div
      v-if="confirmDialog.open"
      class="tc-admin-modal-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="sources-confirm-dialog"
    >
      <div class="tc-admin-modal">
        <header class="tc-admin-modal-header">
          <h2 class="tc-admin-modal-title">{{ confirmDialog.title }}</h2>
          <button
            type="button"
            class="tc-admin-modal-close"
            aria-label="Close"
            :disabled="confirmDialog.busy"
            @click="closeConfirm"
          >&times;</button>
        </header>
        <p>{{ confirmDialog.message }}</p>
        <div class="tc-admin-form-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="confirmDialog.busy"
            @click="closeConfirm"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="confirmDialog.busy"
            data-testid="sources-confirm-action"
            @click="runConfirm"
          >
            <span
              v-if="confirmDialog.busy"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            {{ confirmDialog.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { getSources, disconnectSource } from '../api/sources';
import { SOURCE_META, type SourceStatus } from '../connectors/registry';
import { ApiClientError } from '../api/client';
import { useSourcesStore } from '../stores/sources';
import type { SourceId } from '../api/calendar';
import SourceBadge from './ui/SourceBadge.vue';
import FreshdeskConnectForm from './FreshdeskConnectForm.vue';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const sourcesStore = useSourcesStore();

// ---------------------------------------------------------------------------
// Sources query
// ---------------------------------------------------------------------------
const sourcesQuery = useQuery({
  queryKey: ['sources'],
  queryFn: getSources,
  enabled: computed(() => props.open),
  staleTime: 30_000,
});

const isLoading = computed(() => sourcesQuery.isLoading.value);
const isError = computed(() => sourcesQuery.isError.value);
const errorMessage = computed(() => {
  const err = sourcesQuery.error.value;
  if (err instanceof ApiClientError) return err.message;
  return 'Failed to load sources.';
});
const sources = computed(() => sourcesQuery.data.value?.sources ?? []);

// Enrich sources with meta (connectKind + pitch) and a computed detail line
const enrichedSources = computed(() =>
  sources.value.map(s => {
    const meta = SOURCE_META[s.id];
    const connectKind = meta?.connectKind ?? 'unavailable';
    const pitch       = meta?.pitch ?? '';
    let detailLine: string;
    if (s.status === 'not_connected') {
      detailLine = pitch || 'Not connected';
    } else if (s.status === 'connected') {
      detailLine = 'Connected';
    } else if (s.status === 'reauth_required') {
      detailLine = s.reason ?? 'Reconnect needed';
    } else if (s.status === 'setup_required') {
      detailLine = s.reason ?? 'Needs an admin to set up';
    } else {
      detailLine = s.reason ?? 'Temporary error';
    }
    return { ...s, connectKind, pitch, detailLine };
  }),
);

// ---------------------------------------------------------------------------
// Inline form expansion (Freshdesk api_key_form)
// ---------------------------------------------------------------------------
const expandedFormId = ref<string | null>(null);

function openForm(id: string): void {
  expandedFormId.value = id;
}

// ---------------------------------------------------------------------------
// Disconnect mutation
// ---------------------------------------------------------------------------
const disconnectingId = ref<string | null>(null);

const disconnectMutation = useMutation({
  mutationFn: (id: string) => disconnectSource(id),
  onSuccess: () => {
    disconnectingId.value = null;
    void queryClient.invalidateQueries({ queryKey: ['sources'] });
  },
  onError: () => {
    disconnectingId.value = null;
  },
});

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------
interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => Promise<void> | void;
}

const confirmDialog = reactive<ConfirmState>({
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  busy: false,
  onConfirm: () => {},
});

function openConfirmDialog(opts: {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void> | void;
}): void {
  confirmDialog.open = true;
  confirmDialog.title = opts.title;
  confirmDialog.message = opts.message;
  confirmDialog.confirmLabel = opts.confirmLabel ?? 'Confirm';
  confirmDialog.busy = false;
  confirmDialog.onConfirm = opts.onConfirm;
}

function closeConfirm(): void {
  if (confirmDialog.busy) return;
  confirmDialog.open = false;
}

async function runConfirm(): Promise<void> {
  if (confirmDialog.busy) return;
  confirmDialog.busy = true;
  try {
    await confirmDialog.onConfirm();
  } finally {
    confirmDialog.busy = false;
    confirmDialog.open = false;
  }
}

function handleDisconnect(id: string, label: string): void {
  openConfirmDialog({
    title: `Disconnect ${label}`,
    message: `Disconnect ${label}? Your calendar events will no longer contribute to new drafts until you reconnect.`,
    confirmLabel: 'Disconnect',
    onConfirm: async () => {
      disconnectingId.value = id;
      await disconnectMutation.mutateAsync(id);
    },
  });
}

// ---------------------------------------------------------------------------
// Connect strategies
// ---------------------------------------------------------------------------

// oauth_popup — MUST call window.open synchronously from the click handler.
// Any await before window.open triggers popup-blocker heuristics (ADR-0029 §6).
const OAUTH_START_URL: Partial<Record<SourceId, string>> = {
  outlook: '/api/msgraph/start?return=popup',
  zoom: '/api/zoom/start?return=popup',
};

function connectOAuthPopup(id: string): void {
  const startUrl = OAUTH_START_URL[id as SourceId];
  if (!startUrl) return;
  window.open(startUrl, '_blank', 'width=480,height=720,noopener=no');
  sourcesStore.closeModal();
}

// ---------------------------------------------------------------------------
// Status display helpers
// ---------------------------------------------------------------------------
function pillLabel(status: SourceStatus): string {
  switch (status) {
    case 'connected':       return 'Connected';
    case 'not_connected':   return 'Not connected';
    case 'reauth_required': return 'Reconnect required';
    case 'upstream_error':  return 'Temporary error';
    case 'setup_required':  return 'Setup required';
  }
}

function dotClass(status: SourceStatus): string {
  switch (status) {
    case 'connected':       return 'green';
    case 'not_connected':   return 'neutral';
    case 'reauth_required': return 'red';
    case 'upstream_error':  return 'amber';
    case 'setup_required':  return 'neutral';
  }
}
</script>
